import React, { Component } from 'react'
import { withTranslation } from 'react-i18next';
import Moment from 'moment';
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import employeeService from '../../../../services/Employee.service'
import { actions as employeeActions } from '../../../../actions/employee'
import {formatCurrency, formatPercentage} from "../../../../utils/jsUtils"

import {
  DETAIL_COL_LABEL_COLOR,
  DETAIL_COL_LABEL_SIZE,
  DETAIL_COL_VALUE_WEIGHT,
  GenericDetailColumn
} from "./GenericDetailColumn"
import {DIALOGS} from "../common";
import {DETAIL_TITLE_NUMBER_SIZE, DETAIL_TITLE_NUMBER_WEIGHT, GenericDetailTitle} from "./GenericDetailTitle"
import AwardInfo from "./AwardInfo"

import { Text, Label } from '@amzn/stencil-react-components/text'
import { Col, Row, Spacer, Hr } from '@amzn/stencil-react-components/layout'
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { MessageBanner, MessageBannerType, MessageBannerLink } from '@amzn/stencil-react-components/message-banner'
import { Flyout, FlyoutContent } from "@amzn/stencil-react-components/flyout"
import { Select } from '@amzn/stencil-react-components/form'

class VestedAwardDetailsDialog extends Component {
  onButtonClickHandler = () => {
    this.props.actions.employee.getDetailedTaxDocument('DETAIL_TAX_PDF',this.props.employee.id, null, null, null, [this.props.transaction.id]);
    this.props.onClose();
  }

  onSelectChange = (option) => {
    this.props.onSelectEvent(this.props.events, option.award_id, option.transaction_id)
  }

  getCommonVariables = () => {
    return {
      isRSU: employeeService.isRSU(this.props.award),
      isOption: employeeService.isOption(this.props.award)
    };
  }

  getVestDetails = (t, award, transaction) => {
    const {
      isRSU,
      isOption
    } = this.getCommonVariables();
    const vestedTitle = isRSU ? t('VestedAwardDetailsDialog.RSUsVested') : t('VestedAwardDetailsDialog.OptionsExercised');
    const locale = this.props.locale;

    const totalStrikePrice = formatCurrency(locale, award.strike_price * transaction.shares_transacted);
    const grossProceeds = formatCurrency(locale, transaction.shares_transacted * transaction.fmv);
    const totalFmv = formatCurrency(locale, transaction.fmv, 4, 4);
    const taxableIncome = formatCurrency(locale, transaction.taxable_income);
    const strikePrice = formatCurrency(locale, award.strike_price, 4, 4);

    const brokerLabels = [t('VestingSummary.TaxBrokerLabel'), t('VestingSummary.NetBrokerLabel')];

    return (
      <Col dataTestId="VestDetails" gridGap="S200">
        <GenericDetailTitle title={isRSU ? t('VestedAwardDetailsDialog.VestDetails') :t('VestedAwardDetailsDialog.ExerciseDetails') } />
        <Row>
          <GenericDetailColumn
            label={vestedTitle}
            value={transaction.shares_transacted}
          />
          {isOption &&
            <>
              <Spacer flex={1}/>
              <GenericDetailColumn
                label={t('VestedAwardDetailsDialog.GrossProceeds')}
                value={grossProceeds}
                isOnRight={true}
              />
            </>
          }
        </Row>
        <Row>
          <GenericDetailColumn
            label={t('VestedAwardDetailsDialog.FairMarketValue')}
            value={totalFmv}
          />
          <Spacer flex={1}/>
          <GenericDetailColumn
            label={t('VestedAwardDetailsDialog.TaxableIncome')}
            value={taxableIncome}
            isOnRight={true}
          />
        </Row>
        {isOption &&
          <Row>
            <GenericDetailColumn
              label={t('VestedAwardDetailsDialog.OptionPrice')}
              value={strikePrice}
            />
            <Spacer flex={1}/>
            <GenericDetailColumn
              label={t('VestedAwardDetailsDialog.OptionCosts')}
              value={totalStrikePrice}
              isOnRight={true}
            />
          </Row>
        }
        <Col>
          <Text fontSize={DETAIL_COL_LABEL_SIZE} color={DETAIL_COL_LABEL_COLOR}>
            {t('VestedAwardDetailsDialog.BrokersLabel')}
          </Text>
          <Text fontWeight={DETAIL_COL_VALUE_WEIGHT}>
            {this.props.brokers && this.props.brokers.map((broker, i) =>
              <Text key={i}>{broker}&nbsp;{this.props.brokers.length > 1 && brokerLabels[i]}</Text>
            )}
          </Text>
        </Col>
      </Col>
    );
  }

  getTaxDetails = (t, transaction) => {
    const {
      isRSU,
      isOption
    } = this.getCommonVariables();

    const totalTaxRate = formatPercentage(this.props.locale, transaction.total_taxes_percent, 2, 2);
    const soldTitle = isRSU ? t('VestedAwardDetailsDialog.RSUsSold') : t('VestedAwardDetailsDialog.OptionsSold');
    const sharesSold = transaction.shares_sold === -1 ? '-' : transaction.shares_sold;
    const totalTax = formatCurrency(this.props.locale, transaction.total_tax);

    return (
      <Col dataTestId="TaxDetails" gridGap="S200">
        <GenericDetailTitle title={t('VestedAwardDetailsDialog.TaxDetails')} />
        <Row>
          <GenericDetailColumn
            label={t('VestedAwardDetailsDialog.TotalTaxRate')}
            value={totalTaxRate}
          />
          <Spacer flex={1}/>
            {isRSU &&
              <GenericDetailColumn
                label={t('VestedAwardDetailsDialog.TaxElection')}
                value={t(`TaxTreatment.${transaction.tax_election.code}`)}
                isOnRight={true}
              />
            }
            {isOption &&
              <GenericDetailColumn
                label={t('VestedAwardDetailsDialog.OptionTaxElection')}
                value={transaction.transaction_type_name}
                isOnRight={true}
              />
            }
        </Row>
        <Row>
          <GenericDetailColumn
            label={soldTitle}
            value={sharesSold}
          />
          <Spacer flex={1}/>
          <GenericDetailColumn
            label={t('VestedAwardDetailsDialog.TotalTaxes')}
            value={totalTax}
            isOnRight={true}
          />
        </Row>
      </Col>
    );
  }

  getMessageBanners = (t, transaction, brokerUrl) => {
    const backupWithholding = formatCurrency(this.props.locale, transaction.amount_subject_to_withholding);
    const fractionalCash = formatCurrency(this.props.locale, transaction.fractional_cash_refund);

    return (
      <>
        {transaction.amount_subject_to_withholding > 0 &&
          <MessageBanner type={MessageBannerType.Warning} dataTestId="amount_subject_to_withholding">
            <Row gridGap="S100">
              <Text>{t('VestedAwardDetailsDialog.BackupWitholding', { backup_witholding: backupWithholding })}</Text>
              <MessageBannerLink href={brokerUrl}>
                {t('VestedAwardDetailsDialog.CertifyAtBroker')}
              </MessageBannerLink>
            </Row>
          </MessageBanner>
        }
        {transaction.fractional_cash_refund > 0 &&
          <MessageBanner type={MessageBannerType.Informational} dataTestId="fractional_cash_refund">
            {t('VestedAwardDetailsDialog.FractionalCash', { fractional_cash: fractionalCash})}
          </MessageBanner>
        }
      </>
    );
  }

  getNetTotals = (t, transaction) => {
    const {
      isRSU,
      isOption
    } = this.getCommonVariables();

    if (isRSU && transaction.net_shares > 0) {
      return (
        <Row dataTestId="NetTotals_SharesDeposited" alignItems="center">
          <GenericDetailTitle
            title={t('VestedAwardDetailsDialog.SharesDeposited')}
          />
          <Spacer flex={1}/>
          <Text fontWeight={DETAIL_TITLE_NUMBER_WEIGHT} fontSize={DETAIL_TITLE_NUMBER_SIZE}>
            {transaction.net_shares}
          </Text>
        </Row>
      );
    } else if (isOption) {
      return (
        <>
          {transaction.net_proceeds > 0 &&
            <Row dataTestId="NetTotals_NetProceeds" alignItems="center">
              <GenericDetailTitle
                title={t('VestedAwardDetailsDialog.NetProceeds')}
              />
              <Spacer flex={1}/>
              <Text fontWeight={DETAIL_TITLE_NUMBER_WEIGHT} fontSize={DETAIL_TITLE_NUMBER_SIZE}>
                {formatCurrency(this.props.locale, transaction.net_proceeds)}
              </Text>
            </Row>
          }
          {transaction.net_shares > 0 &&
            <Row alignItems="center">
              <GenericDetailTitle
                title={t('VestedAwardDetailsDialog.OptionsDeposited')}
              />
              <Spacer flex={1}/>
              <Text fontWeight={DETAIL_TITLE_NUMBER_WEIGHT} fontSize={DETAIL_TITLE_NUMBER_SIZE}>
                {transaction.net_shares}
              </Text>
            </Row>
          }
        </>
      );
    }
  }

  renderSelect = (t) => {
    if (!this.props.events || this.props.events.length <= 1) {
      return null;
    }

    return (
      <Col dataTestId="VestedAwardDetailsDialog_select_VestEvent">
        <Label>
          {t("VestedAwardDetailsDialog.VestEvent")}
        </Label>
        <Select
          options={this.props.events}
          renderOption={this.renderEventLabel}
          renderNativeOption={this.renderEventLabel}
          renderSelectedValue={this.renderEventLabel}
          valueAccessor="award_name"
          onChange={this.onSelectChange}
        />
      </Col>
    )

  }

  renderEventLabel = (event) => {
    return this.props.t("VestingSummary.NextVestEventRow", {award_name: event.award_name, share_quatity: event.share_quantity })
  }

  render() {
    if (!this.props.visible) {
      return null;
    }

    const { t, award, transaction, employee } = this.props;
    const brokerUrl = employeeService.getEmployeeBrokerUrl(employee);

    return (
      <Flyout dataTestId="VestedAwardDetailsDialog_test_id" isOpen={this.props.visible} close={this.props.onClose}>
        <FlyoutContent
          dataTestId="VestedAwardDetailsDialog_close_btn"
          titleText={t('VestedAwardDetailsDialog.Title', { vest_date: Moment(this.props.transaction ? transaction.transaction_date : this.props.date).format('ll') })}
          closeButtonAltText="Close Button For Vested Award Details Dialog"
          onCloseButtonClick={this.props.onClose}
          buttons={[
            <Button key="PDFButton" onClick={this.onButtonClickHandler} variant={ButtonVariant.Primary} disabled={this.props.transaction === null}>
              {t('VestedAwardDetailsDialog.ViewAsPDF')}
            </Button>
          ]}
        >
          <Col gridGap="S400">
            {this.renderSelect(t)}

            {award && transaction &&
              <>
                <AwardInfo
                  award={award}
                  dialogName={DIALOGS.VestedAwardDetailsDialog}
                  locale={this.props.locale}
                />
                {this.getMessageBanners(t, transaction, brokerUrl)}
                <Hr/>
                {this.getVestDetails(t, award, transaction)}
                <Hr/>
                {this.getTaxDetails(t, transaction)}
                <Hr/>
                {this.getNetTotals(t, transaction)}
              </>
            }
          </Col>
        </FlyoutContent>
      </Flyout>
    );
  }
}

const mapState = state => ({
  employee: state.employee,
  locale: state.employee.language
})

const mapDispatchToProps = dispatch => ({
  actions: {
    employee: bindActionCreators(employeeActions, dispatch),
  }
})

export default connect(mapState, mapDispatchToProps)((withTranslation('translationsV2')(VestedAwardDetailsDialog)))
